import { createContext, useContext, useState, useCallback, useMemo, useEffect, useRef, type ReactNode } from 'react'
import { createApiClient, type ApiClient } from '../api/client'
import type { DecodedToken } from '../api/types'
import {
  signIn as cognitoSignIn,
  completeNewPassword as cognitoCompleteNewPassword,
  refreshSession,
  signOut as cognitoSignOut,
  isCognitoConfigured,
  type CognitoTokens,
  type CognitoAuthResult,
  type CognitoAuthSuccess,
} from './cognito'
import type { CognitoUser } from 'amazon-cognito-identity-js'

interface AuthState {
  token: string | null
  refreshToken: string | null
  tokenExpiresAt: number | null
  username: string | null
}

interface AuthContextValue {
  isAuthenticated: boolean
  isAdmin: boolean
  isSuperUser: boolean
  user: DecodedToken | null
  apiClient: ApiClient | null
  isCognitoAvailable: boolean
  loginWithCognito: (username: string, password: string) => Promise<CognitoAuthResult>
  completeNewPassword: (cognitoUser: CognitoUser, newPassword: string) => Promise<void>
  loginWithToken: (token: string) => void
  logout: () => void
}

const AuthContext = createContext<AuthContextValue | null>(null)

const STORAGE_KEY = 'violet_research_auth'
const ADMIN_ROLES = ['db_admin', 'admin']
const SUPER_USER_ROLES = ['su'] // Super users can access migrations and other privileged features
const TOKEN_REFRESH_MARGIN = 5 * 60 * 1000

function decodeJwt(token: string): DecodedToken | null {
  try {
    const parts = token.split('.')
    if (parts.length !== 3) return null
    const payload = JSON.parse(atob(parts[1]))
    return payload as DecodedToken
  } catch {
    return null
  }
}

function loadAuthState(): AuthState {
  try {
    const stored = localStorage.getItem(STORAGE_KEY)
    if (stored) {
      return JSON.parse(stored)
    }
  } catch {
    // Ignore errors
  }
  return {
    token: null,
    refreshToken: null,
    tokenExpiresAt: null,
    username: null,
  }
}

function saveAuthState(state: AuthState): void {
  localStorage.setItem(STORAGE_KEY, JSON.stringify(state))
}

function clearAuthState(): void {
  localStorage.removeItem(STORAGE_KEY)
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const [authState, setAuthState] = useState<AuthState>(loadAuthState)
  const refreshTimeoutRef = useRef<number | null>(null)
  // Refs for the API client's token getter (always returns latest token)
  const authStateRef = useRef<AuthState>(authState)
  // Lock to prevent concurrent refresh attempts
  const refreshPromiseRef = useRef<Promise<string | null> | null>(null)

  // Keep ref in sync with state for the token getter
  useEffect(() => {
    authStateRef.current = authState
  }, [authState])

  const scheduleTokenRefresh = useCallback((expiresAt: number, username: string, token: string) => {
    if (refreshTimeoutRef.current) {
      window.clearTimeout(refreshTimeoutRef.current)
    }

    const timeUntilRefresh = expiresAt - Date.now() - TOKEN_REFRESH_MARGIN
    if (timeUntilRefresh <= 0) {
      refreshSession(username, token)
        .then((tokens: CognitoTokens) => {
          const newState: AuthState = {
            token: tokens.idToken,
            refreshToken: tokens.refreshToken,
            tokenExpiresAt: tokens.expiresAt,
            username: username,
          }
          setAuthState(newState)
          saveAuthState(newState)
          scheduleTokenRefresh(tokens.expiresAt, username, tokens.refreshToken)
        })
        .catch(() => {
          setAuthState({ token: null, refreshToken: null, tokenExpiresAt: null, username: null })
          clearAuthState()
        })
      return
    }

    refreshTimeoutRef.current = window.setTimeout(() => {
      refreshSession(username, token)
        .then((tokens: CognitoTokens) => {
          const newState: AuthState = {
            token: tokens.idToken,
            refreshToken: tokens.refreshToken,
            tokenExpiresAt: tokens.expiresAt,
            username: username,
          }
          setAuthState(newState)
          saveAuthState(newState)
          scheduleTokenRefresh(tokens.expiresAt, username, tokens.refreshToken)
        })
        .catch(() => {
          setAuthState({ token: null, refreshToken: null, tokenExpiresAt: null, username: null })
          clearAuthState()
        })
    }, timeUntilRefresh)
  }, [])

  /**
   * Refresh the token for API client use.
   * Handles concurrent requests by returning the same promise if refresh is in progress.
   * Returns the new token or null if refresh fails.
   */
  const refreshTokenForApi = useCallback(async (): Promise<string | null> => {
    // If refresh is already in progress, wait for it
    if (refreshPromiseRef.current) {
      return refreshPromiseRef.current
    }

    const { username, refreshToken: currentRefreshToken } = authStateRef.current
    if (!username || !currentRefreshToken) {
      return null
    }

    // Start refresh and store the promise (lock)
    refreshPromiseRef.current = (async () => {
      try {
        const tokens = await refreshSession(username, currentRefreshToken)
        const newState: AuthState = {
          token: tokens.idToken,
          refreshToken: tokens.refreshToken,
          tokenExpiresAt: tokens.expiresAt,
          username: username,
        }
        setAuthState(newState)
        saveAuthState(newState)
        authStateRef.current = newState
        scheduleTokenRefresh(tokens.expiresAt, username, tokens.refreshToken)
        return tokens.idToken
      } catch {
        // Refresh failed - clear auth state and return null
        setAuthState({ token: null, refreshToken: null, tokenExpiresAt: null, username: null })
        clearAuthState()
        authStateRef.current = { token: null, refreshToken: null, tokenExpiresAt: null, username: null }
        return null
      } finally {
        // Clear the lock
        refreshPromiseRef.current = null
      }
    })()

    return refreshPromiseRef.current
  }, [scheduleTokenRefresh])

  useEffect(() => {
    const { refreshToken, tokenExpiresAt, username } = authState
    if (refreshToken && tokenExpiresAt && username) {
      scheduleTokenRefresh(tokenExpiresAt, username, refreshToken)
    }
    return () => {
      if (refreshTimeoutRef.current) {
        window.clearTimeout(refreshTimeoutRef.current)
      }
    }
  }, [])

  // Proactively refresh token when tab regains focus if it's expired or about to expire
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        const { tokenExpiresAt, refreshToken: currentRefreshToken, username } = authStateRef.current
        if (!tokenExpiresAt || !currentRefreshToken || !username) return

        // If token is expired or will expire within the margin, refresh immediately
        const timeUntilExpiry = tokenExpiresAt - Date.now()
        if (timeUntilExpiry <= TOKEN_REFRESH_MARGIN) {
          refreshTokenForApi()
        }
      }
    }

    document.addEventListener('visibilitychange', handleVisibilityChange)
    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange)
    }
  }, [refreshTokenForApi])

  const user = useMemo<DecodedToken | null>(() => {
    if (authState.token) {
      return decodeJwt(authState.token)
    }
    return null
  }, [authState])

  const isAuthenticated = useMemo(() => {
    return !!authState.token
  }, [authState])

  const isAdmin = useMemo(() => {
    if (!user) return false
    const groups = user['cognito:groups'] || []
    return groups.some(g => ADMIN_ROLES.includes(g))
  }, [user])

  const isSuperUser = useMemo(() => {
    if (!user) return false
    const groups = user['cognito:groups'] || []
    return groups.some(g => SUPER_USER_ROLES.includes(g))
  }, [user])

  const apiClient = useMemo<ApiClient | null>(() => {
    if (authState.token) {
      return createApiClient({
        getToken: () => authStateRef.current.token,
        refreshToken: refreshTokenForApi,
      })
    }
    return null
  }, [authState.token, refreshTokenForApi])

  const handleSuccessfulAuth = useCallback((result: CognitoAuthSuccess) => {
    const newState: AuthState = {
      token: result.tokens.idToken,
      refreshToken: result.tokens.refreshToken,
      tokenExpiresAt: result.tokens.expiresAt,
      username: result.username,
    }
    setAuthState(newState)
    saveAuthState(newState)
    scheduleTokenRefresh(result.tokens.expiresAt, result.username, result.tokens.refreshToken)
  }, [scheduleTokenRefresh])

  const loginWithCognito = useCallback(async (username: string, password: string): Promise<CognitoAuthResult> => {
    const result = await cognitoSignIn(username, password)
    if (result.type === 'success') {
      handleSuccessfulAuth(result)
    }
    return result
  }, [handleSuccessfulAuth])

  const completeNewPassword = useCallback(async (cognitoUser: CognitoUser, newPassword: string): Promise<void> => {
    const result = await cognitoCompleteNewPassword(cognitoUser, newPassword)
    handleSuccessfulAuth(result)
  }, [handleSuccessfulAuth])

  const loginWithToken = useCallback((token: string) => {
    const decoded = decodeJwt(token)
    if (!decoded) {
      throw new Error('Invalid JWT token')
    }
    const newState: AuthState = {
      token,
      refreshToken: null,
      tokenExpiresAt: null,
      username: null,
    }
    setAuthState(newState)
    saveAuthState(newState)
  }, [])

  const logout = useCallback(() => {
    if (refreshTimeoutRef.current) {
      window.clearTimeout(refreshTimeoutRef.current)
    }
    cognitoSignOut()
    setAuthState({
      token: null,
      refreshToken: null,
      tokenExpiresAt: null,
      username: null,
    })
    clearAuthState()
  }, [])

  const value: AuthContextValue = {
    isAuthenticated,
    isAdmin,
    isSuperUser,
    user,
    apiClient,
    isCognitoAvailable: isCognitoConfigured(),
    loginWithCognito,
    completeNewPassword,
    loginWithToken,
    logout,
  }

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>
}

export function useAuth(): AuthContextValue {
  const context = useContext(AuthContext)
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider')
  }
  return context
}
